// src/pages/admin/Lending.tsx
//
// Equipment lending desk — LIVE Shop backend (/api/lending). Loan status
// is computed server-side (returned when returned_at is set, overdue
// when due_at < today and not returned, else out). Creating loans and
// marking returns require the admin write role.

import { useCallback, useEffect, useState } from "react";
import { PackageOpen, Plus, RefreshCcw, Undo2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, type BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Input";
import { CustomerPicker, type CustomerSelection } from "@/components/opsdesk/CustomerPicker";
import { useAuth } from "@/components/auth/AuthContext";
import { cn } from "@/lib/cn";
import {
  createLoan,
  fetchLoans,
  returnLoan,
  type EquipmentLoanRow,
  type LoanStatus,
} from "@/lib/opsdesk/api";

const STATUS_LABEL_KEY: Record<LoanStatus, string> = {
  out: "admin.lending.statusOut",
  overdue: "admin.lending.statusOverdue",
  returned: "admin.lending.statusReturned",
};

const STATUS_VARIANT: Record<LoanStatus, BadgeVariant> = {
  out: "teal",
  overdue: "danger",
  returned: "default",
};

const STATUS_ORDER: LoanStatus[] = ["out", "overdue", "returned"];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

export function AdminLending() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [loans, setLoans] = useState<EquipmentLoanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<LoanStatus | "all">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await fetchLoans(statusFilter);
      setLoans(body.rows);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (auth.isReady && auth.isAuthenticated) void refresh();
  }, [auth.isReady, auth.isAuthenticated, refresh]);

  async function doReturn(loan: EquipmentLoanRow) {
    setBusyId(loan.loan_id);
    setError("");
    try {
      await returnLoan(loan.loan_id);
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <PackageOpen
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.lending.title")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {loans.length} {loans.length === 1 ? t("admin.lending.loanOne") : t("admin.lending.loanOther")}
            {statusFilter !== "all"
              ? ` — ${t(STATUS_LABEL_KEY[statusFilter])}`
              : ""}
            {t("admin.lending.statusNote")}
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("admin.lending.newLoan")}
        </Button>
      </header>

      {showCreate && (
        <CreateLoanForm
          onCreated={() => {
            setShowCreate(false);
            void refresh();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-fg-quaternary">{t("admin.lending.statusLabel")}</span>
        {(["all", ...STATUS_ORDER] as Array<LoanStatus | "all">).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            aria-pressed={statusFilter === s}
            className={cn(
              "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors",
              statusFilter === s
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
            )}
          >
            {s === "all" ? t("admin.lending.all") : t(STATUS_LABEL_KEY[s])}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-md border border-border bg-bg-canvas/30 p-3 text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-3.5 w-3.5 animate-spin" />
          {t("admin.lending.loadingLoans")}
        </div>
      ) : loans.length === 0 ? (
        <EmptyState
          icon={
            <PackageOpen
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
          }
          title={t("admin.lending.emptyTitle")}
          description={t("admin.lending.emptyDescription")}
        />
      ) : (
        <div className="space-y-2.5">
          {loans.map((loan) => (
            <Card key={loan.loan_id}>
              <CardContent className="grid gap-3 p-4 sm:grid-cols-[1.2fr_2fr_auto] sm:items-center">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] text-fg-quaternary">
                    {loan.loan_id}
                  </div>
                  <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
                    {loan.customer_name}
                  </div>
                  <div className="font-mono text-[11px] text-fg-tertiary">
                    {t("admin.lending.productRef")}{loan.product_id} × {loan.quantity}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Badge variant={STATUS_VARIANT[loan.status]} size="sm">
                    {t(STATUS_LABEL_KEY[loan.status])}
                  </Badge>
                  <div className="text-[11px] text-fg-tertiary">
                    {t("admin.lending.outLabel")} {fmtDate(loan.out_at)} · {t("admin.lending.dueLabel")} {fmtDate(loan.due_at)}
                    {loan.returned_at ? ` · ${t("admin.lending.returnedLabel")} ${fmtDate(loan.returned_at)}` : ""}
                  </div>
                  {loan.note && (
                    <p className="line-clamp-1 text-[10px] text-fg-quaternary">
                      {loan.note}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {loan.status !== "returned" ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === loan.loan_id}
                      onClick={() => void doReturn(loan)}
                    >
                      {busyId === loan.loan_id ? (
                        <RefreshCcw className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <Undo2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      )}
                      {t("admin.lending.markReturned")}
                    </Button>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
                      {t("admin.lending.statusReturned")}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateLoanForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<CustomerSelection | null>(null);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [dueAt, setDueAt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const productIdValid = /^\d+$/.test(productId.trim());
  const qty = Number(quantity);
  const qtyValid = Number.isInteger(qty) && qty > 0;
  const canSubmit =
    customer !== null && productIdValid && qtyValid && !busy;

  async function submit() {
    if (!customer) return;
    setBusy(true);
    setError("");
    try {
      await createLoan({
        customer_id: customer.customer_id,
        product_id: Number(productId.trim()),
        quantity: qty,
        due_at: dueAt === "" ? null : dueAt,
        note: note.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
          {t("admin.lending.newEquipmentLoan")}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.lending.customer")}
            </label>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                {t("admin.lending.productId")}
              </label>
              <Input
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder={t("admin.lending.productIdPlaceholder")}
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
                {t("admin.lending.quantity")}
              </label>
              <Input
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputMode="numeric"
              />
            </div>
          </div>
        </div>
        {productId.trim() !== "" && !productIdValid && (
          <p className="text-[10px] text-accent-rose">
            {t("admin.lending.productIdNumber")}
          </p>
        )}
        {!qtyValid && (
          <p className="text-[10px] text-accent-rose">
            {t("admin.lending.quantityError")}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.lending.dueDateOptional")}
            </label>
            <Input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.lending.noteOptional")}
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("admin.lending.notePlaceholder")}
            />
          </div>
        </div>
        {error && (
          <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-xs text-accent-rose">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("admin.lending.cancel")}
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => void submit()}>
            {t("admin.lending.createLoan")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
